import { create } from 'zustand';

// 1) Base entity type
interface BaseEntity {
  id: string;
}

// 2) Generic store interface
interface GenericStore<T extends BaseEntity> {
  items: Record<string, T>;
  loading: boolean;
  error: string | null;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  totalItems: number;
  setTotalItems: (total: number) => void;
  setItems: (items: T[]) => void;
  addItem: (item: T) => void;
  updateItem: (item: Partial<T> & { id: string }) => void;
  deleteItem: (id: string) => void;
}

// 3) Create generic Zustand store
export function createGenericStore<T extends BaseEntity>() {
  return create<GenericStore<T>>((set) => ({
    items: {},
    loading: false,
    error: null,
    totalItems: 0,
    setLoading: (loading) => set({ loading }),
    setError: (error) => set({ error }),
    setTotalItems: (total) => set({ totalItems: total }),
    // ✅ Set items (only if different)
    setItems: (itemsArr) => {
      const newItems: Record<string, T> = {};
      for (const item of itemsArr) newItems[item.id] = item;
      set({ items: newItems });
    },

    // ✅ Add item (only if new)
    addItem: (item) => {
      set((state) => {
        if (!state.items[item.id]) {
          return { items: { ...state.items, [item.id]: item } };
        }
        return state;
      });
    },

    // ✅ Update item (only if changed)
    updateItem: (item) => {
      set((state) => {
        const oldItem = state.items[item.id];
        if (!oldItem) return state;

        const updatedItem = { ...oldItem, ...item };
        if (JSON.stringify(updatedItem) !== JSON.stringify(oldItem)) {
          return { items: { ...state.items, [item.id]: updatedItem } };
        }
        return state;
      });
    },

    // ✅ Delete item (only if exists)
    deleteItem: (id) => {
      set((state) => {
        if (state.items[id]) {
          const newItems = { ...state.items };
          delete newItems[id];
          return { items: newItems };
        }
        return state;
      });
    },
  }));
}
